import { useCallback, useEffect, useRef } from 'react';

interface DebounceOptions {
  /**
   * The delay in milliseconds before the debounced function is executed.
   *
   * @default 500
   */
  delay?: number;

  /**
   * The maximum time to wait between function executions in milliseconds.
   *
   * When provided, acts as a throttle mechanism - the function will execute
   * AT MOST every `maxWaitMs` milliseconds, even during continuous calls.
   * This prevents endless debouncing during sustained user interactions.
   *
   * @example
   * // For ResizeObserver - execute at most every 16ms (60fps)
   * { maxWaitMs: 16 }
   *
   * @default undefined (no maximum wait time)
   */
  maxWaitMs?: number;
}

interface DebounceControls {
  /**
   * Cancels any pending debounced function execution.
   * The function will not be called until debounced again.
   */
  cancel: () => void;

  /**
   * Immediately executes the pending debounced function with the latest arguments.
   * Clears any pending timeouts.
   */
  flush: () => void;

  /**
   * Returns true if there is a pending debounced function execution.
   * Useful for showing loading states or preventing duplicate actions.
   */
  isPending: () => boolean;
}

/**
 * A debounced function with additional control methods.
 *
 * @template T - The type of the original function
 */
type DebouncedFn<T extends (...args: readonly any[]) => any> = ((
  ...args: Parameters<T>
) => ReturnType<T>) &
DebounceControls;

/**
 * Creates a debounced version of a function that delays execution until after
 * the specified delay period has elapsed since the last time it was invoked.
 *
 * The returned function maintains referential stability and always captures
 * the latest values from closures, eliminating the need for manual `useCallback`.
 *
 * @returns A debounced function with control methods (cancel, flush, isPending).
 *
 * @example
 * // Basic usage - search input
 * const debouncedSearch = useDebouncedCallback(
 *   (query: string) => {
 *     console.log('Searching for:', query);
 *   },
 *   { delay: 300 }
 * );
 *
 * @example
 * // With maxWait for ResizeObserver (60fps throttling)
 * const debouncedResize = useDebouncedCallback(
 *   (entries: ResizeObserverEntry[]) => {
 *     // Handle resize - fires at most every 16ms during continuous resizing
 *     updateLayout(entries);
 *   },
 *   { delay: 100, maxWaitMs: 16 }
 * );
 *
 * @example
 * // Using control functions
 * const debouncedSave = useDebouncedCallback(
 *   (data: FormData) => saveToServer(data),
 *   { delay: 1000 }
 * );
 *
 * // Cancel pending save
 * debouncedSave.cancel();
 *
 * // Force immediate save
 * debouncedSave.flush();
 *
 * // Check if save is pending
 * if (debouncedSave.isPending()) {
 *   showSpinner();
 * }
 *
 * @example
 * // Safe to use in dependency arrays (stable reference)
 * useEffect(() => {
 *   const observer = new ResizeObserver(debouncedResize);
 *   observer.observe(elementRef.current);
 *   return () => observer.disconnect();
 * }, [debouncedResize]); // ✅ Won't cause re-runs
 */
export const useDebouncedCallback = <TFn extends (...args: any[]) => any>(
  func: TFn,
  options: DebounceOptions = {},
): DebouncedFn<TFn> => {
  const { delay = 500, maxWaitMs } = options;

  const funcRef = useRef(func);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const argsRef = useRef<Parameters<TFn> | null>(null);

  // Update function ref when func changes to always capture latest closure values
  useEffect(() => {
    funcRef.current = func;
  }, [func]);

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
    }
  }, []);

  const executeCallback = useCallback(() => {
    const args = argsRef.current;
    if (args) {
      argsRef.current = null;
      funcRef.current(...args);
    }
  }, []);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    argsRef.current = null;
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current || maxTimeoutRef.current) {
      cancel();
      executeCallback();
    }
  }, [cancel, executeCallback]);

  const isPending = useCallback(() => timeoutRef.current !== null, []);

  const debounced = useCallback(
    (...args: Parameters<TFn>) => {
      // Store the latest arguments for execution
      argsRef.current = args;

      // Cancel existing regular timeout (debounce behavior)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set up maxWait timeout if this is the first call in a sequence
      // This acts as a throttle to prevent endless debouncing
      if (maxWaitMs && !maxTimeoutRef.current) {
        maxTimeoutRef.current = setTimeout(() => {
          maxTimeoutRef.current = null;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          executeCallback();
        }, maxWaitMs);
      }

      // Set new regular timeout (standard debounce behavior)
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (maxTimeoutRef.current) {
          clearTimeout(maxTimeoutRef.current);
          maxTimeoutRef.current = null;
        }
        executeCallback();
      }, delay);
    },
    [delay, maxWaitMs, executeCallback],
  );

  // Attach control functions to the debounced function
  const debouncedWithControls = debounced as DebouncedFn<TFn>;
  debouncedWithControls.cancel = cancel;
  debouncedWithControls.flush = flush;
  debouncedWithControls.isPending = isPending;

  return debouncedWithControls;
};
